import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip'
import { cn } from '@/lib/utils'
import { ChatContextEventName, ChatContextState, getContextUsagePercent } from './types'

interface ContextWindowIndicatorProps {
  contextState: ChatContextState | null
  lastContextEvent?: ChatContextEventName | null
  className?: string
}

function getUsageTone(usagePercent: number): {
  strokeClassName: string
  trackClassName: string
} {
  if (usagePercent >= 85) {
    return {
      strokeClassName: 'stroke-destructive',
      trackClassName: 'stroke-destructive/15',
    }
  }
  if (usagePercent >= 70) {
    return {
      strokeClassName: 'stroke-amber-600 dark:stroke-amber-400',
      trackClassName: 'stroke-amber-600/20 dark:stroke-amber-400/5',
    }
  }
  return {
    strokeClassName: 'stroke-muted-foreground/40',
    trackClassName: 'stroke-muted-foreground/10',
  }
}

function formatTokenCount(value: number): string {
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(1)}k`
  }
  return String(value)
}

function buildTooltipContent(
  state: ChatContextState,
  usagePercent: number,
  lastContextEvent: ChatContextEventName | null | undefined
): string {
  const used = formatTokenCount(state.estimatedTokens)
  const total = formatTokenCount(state.contextWindow)
  const summaryLine = state.hasWarmSummary
    ? 'Includes a warm summary of earlier turns.'
    : 'All recent turns are verbatim.'
  const hotLine = `${state.hotMessageCount} recent message${state.hotMessageCount === 1 ? '' : 's'} in agent context.`
  const eventLine = lastContextEvent ? `Last context event: ${lastContextEvent}.` : ''

  return `${usagePercent}% context used (${used} / ${total} tokens). ${hotLine} ${summaryLine}${eventLine ? ` ${eventLine}` : ''}`
}

/**
 * Compact circular gauge for agent context window usage.
 */
export function ContextWindowIndicator({
  contextState,
  lastContextEvent = null,
  className,
}: ContextWindowIndicatorProps) {
  if (!contextState) return null

  const usagePercent = getContextUsagePercent(contextState)
  if (usagePercent === null) return null

  const size = 28
  const strokeWidth = 3
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const dashOffset = circumference - (usagePercent / 100) * circumference
  const tone = getUsageTone(usagePercent)

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={cn('relative flex size-7 shrink-0 items-center justify-center', className)}
          role="img"
          aria-label={`${usagePercent}% of the agent context window used`}
        >
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              strokeWidth={strokeWidth}
              className={tone.trackClassName}
            />
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              className={cn('transition-[stroke-dashoffset] duration-300', tone.strokeClassName)}
            />
          </svg>
          {/* <span className="pointer-events-none absolute text-[9px] font-semibold tabular-nums text-muted-foreground">
            {usagePercent}%
          </span> */}
        </div>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs text-xs">
        {buildTooltipContent(contextState, usagePercent /* , lastContextEvent */, null)}
      </TooltipContent>
    </Tooltip>
  )
}
